import React, { useState, useMemo, useRef } from "react";
import StudentList from "../../components/checkIn/StudentList";
import { MessageSquare, Search } from "lucide-react";
import toast from "react-hot-toast";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import StudentDetailsModal from "../../components/checkIn/StudentDetailsModal";

// Context & Hooks
import { useAuth } from "../../context/auth";
import { useApi, useMutation } from "../../hooks";

// Services
import { classesService, checkinService } from "../../api/services";
import { getFileUrl } from "../../api/config";
import { isNewRegistration, sortClassesByName } from "../../utils/format";
import { classesOf } from "../../utils/classGroups";
import useClassGroups from "../../hooks/useClassGroups";

/** Today as local YYYY-MM-DD (toISOString would give the UTC day). */
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const PER_PAGE_STORAGE_KEY = "csf_checkin_per_page";
const PER_PAGE_OPTIONS = [5, 10, 15, 30, 50];

const CheckIn = () => {
  const { user } = useAuth();
  const [openModal, setOpenModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Alphabetical");
  const [selectedClass, setSelectedClass] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [uncheckedIds, setUncheckedIds] = useState(new Set());

  // ============================================================================
  // API DATA FETCHING
  // ============================================================================

  // Fetch coach's assigned classes
  const { data: classesData, loading: loadingClasses } = useApi(
    () => classesService.getAll({ coach_id: user?.id }),
    {
      initialData: { items: [] },
      dependencies: [user?.id],
      autoFetch: !!user?.id,
    },
  );

  // Extract classes array from response
  const classes = useMemo(
    () =>
      sortClassesByName(
        Array.isArray(classesData) ? classesData : classesData?.items || [],
      ),
    [classesData],
  );

  // One sheet for every membership of a TDC site, listed above the classes
  const classGroups = useClassGroups(classes);
  const classOptions = useMemo(
    () => [...classGroups, ...classes],
    [classGroups, classes],
  );

  // Set first class as default when classes load (useEffect for side effects)
  React.useEffect(() => {
    if (classes?.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
    }
  }, [classes]);

  // Roster page size is the coach's preference, not per-class state: it
  // survives switching classes and reloading the page.
  const [perPage, setPerPage] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(PER_PAGE_STORAGE_KEY));
      return PER_PAGE_OPTIONS.includes(saved) ? saved : 10;
    } catch {
      return 10;
    }
  });
  const changePerPage = (n) => {
    setPerPage(n);
    try {
      localStorage.setItem(PER_PAGE_STORAGE_KEY, String(n));
    } catch {
      /* private mode / storage blocked - keep it for this session only */
    }
  };

  // Which class the roster on screen belongs to. The loading skeleton is
  // only for a class's FIRST load; a refetch after a check-in must keep
  // the list mounted, or the coach's page and per-page choice are wiped
  // (reported 2026-09-15: "pick 30 per page, tap a name, back to 10").
  const [loadedClassId, setLoadedClassId] = useState(null);
  const requestedClassIdRef = useRef(null);
  requestedClassIdRef.current = selectedClass?.id || null;

  // Fetch check-in status for selected class
  const {
    data: checkInStatus,
    loading: loadingStudents,
    refetch: refetchStatus,
    error: checkInError,
  } = useApi(
    // A combined sheet is every member class's roster, each child tagged with
    // their own class. Promise.all on purpose: if one roster fails the sheet
    // must say so rather than quietly show the children it did get.
    () =>
      Promise.all(
        // Check-in is always today: only the memberships that meet today
        classesOf(selectedClass, localToday()).map(async (cls) => {
          const statuses = await checkinService.getClassStatus(cls.id);
          const list = Array.isArray(statuses)
            ? statuses
            : statuses?.statuses || statuses?.items || [];
          return list.map((item) => ({
            ...item,
            class_id: cls.id,
            class_name: cls.name,
          }));
        }),
      ).then((rosters) => rosters.flat()),
    {
      initialData: [],
      dependencies: [selectedClass?.id],
      autoFetch: !!selectedClass?.id,
      onSuccess: () => {
        setLoadedClassId(requestedClassIdRef.current);
      },
      onError: (error) => {
        console.error("Failed to fetch check-in status:", error);
      },
    },
  );
  const isFirstLoadForClass =
    loadingStudents && loadedClassId !== (selectedClass?.id || null);

  // Debug log for troubleshooting roster data issues
  React.useEffect(() => {
    if (selectedClass?.id && !loadingStudents) {
      console.log(
        "Check-in status for class",
        selectedClass.name,
        ":",
        checkInStatus,
      );
    }
  }, [selectedClass, checkInStatus, loadingStudents]);

  // Check-in mutation
  const { mutate: toggleCheckIn, loading: checkingIn } = useMutation(
    checkinService.checkIn,
    {
      onSuccess: () => {
        refetchStatus();
      },
    },
  );

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Transform check-in status to student format for StudentList
  const students = useMemo(() => {
    // Handle different response formats
    let statusArray = [];
    if (Array.isArray(checkInStatus)) {
      statusArray = checkInStatus;
    } else if (
      checkInStatus?.statuses &&
      Array.isArray(checkInStatus.statuses)
    ) {
      statusArray = checkInStatus.statuses;
    } else if (checkInStatus?.items && Array.isArray(checkInStatus.items)) {
      statusArray = checkInStatus.items;
    }

    if (statusArray.length === 0) {
      console.log("No students found in check-in status:", checkInStatus);
      return [];
    }

    return statusArray.map((item) => {
      const profileImage =
        item.child?.profile_image || item.profile_image || null;
      const firstName =
        item.child_first_name ||
        item.child?.first_name ||
        item.first_name ||
        "";
      const lastName =
        item.child_last_name || item.child?.last_name || item.last_name || "";
      const childName = item.child_name || `${firstName} ${lastName}`.trim();
      const isChecked =
        item.is_checked_in || item.checked_in || item.checked || false;
      const enrollId = item.enrollment_id || item.id;

      // Build parent object from flat API fields or nested object
      const parent = item.parent || {
        first_name: item.parent_name?.split(" ")[0] || "",
        last_name: item.parent_name?.split(" ").slice(1).join(" ") || "",
        phone: item.parent_phone || null,
        email: item.parent_email || null,
      };

      return {
        id: enrollId,
        enrollment_id: enrollId,
        classId: item.class_id || null,
        // Shown on a combined sheet only, where it tells memberships apart
        className: selectedClass?.isGroup ? item.class_name : null,
        name: childName || "Unknown",
        grade: item.child?.grade || item.grade || "-",
        groupNumber: item.group_number ?? item.child?.group_number ?? null,
        checked: isChecked,
        isNew: isNewRegistration(item.enrolled_at),
        wasUnchecked: !isChecked && uncheckedIds.has(enrollId),
        checkInId: item.check_in_id || item.checkin_id || null,
        img: profileImage ? getFileUrl(profileImage) : null,
        dob: item.child_dob || item.child?.date_of_birth || null,
        // Additional data for modal
        child: item.child,
        parent,
        medical_info:
          item.medical_conditions ||
          item.medical_info ||
          item.child?.medical_info,
        hasMedicalAlert: item.has_medical_alert || false,
        hasNoMedicalConditions: item.has_no_medical_conditions || false,
        afterSchool:
          item.after_school || item.child?.after_school_attendance || false,
        afterSchoolProgram: item.after_school_program || null,
        // Who to call when the parent cannot be reached. Coaches asked for
        // these on the pitch, which is exactly when nobody can go looking.
        emergencyContacts: item.emergency_contacts || [],
        notes: item.notes,
      };
    });
  }, [checkInStatus, uncheckedIds, selectedClass]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleClassChange = (classItem) => {
    setSelectedClass(classItem);
    setDropdownOpen(false);
  };

  const handleOpenModal = (student) => {
    setSelectedStudent(student);
    setOpenModal(true);
  };

  const handleCheckIn = async (student) => {
    try {
      // Track toggle state for visual feedback
      if (student.checked) {
        setUncheckedIds((prev) => new Set([...prev, student.enrollment_id]));
      } else {
        setUncheckedIds((prev) => {
          const next = new Set(prev);
          next.delete(student.enrollment_id);
          return next;
        });
      }
      await toggleCheckIn({
        enrollment_id: student.enrollment_id,
        // The child's own class: a combined sheet has no class of its own
        class_id: student.classId || selectedClass?.id,
        check_in_date: new Date().toISOString().split("T")[0],
      });
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error("Failed to update check-in. Please try again.");
      // Revert the optimistic UI update
      refetchStatus();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="min-h-screen max-sm:h-fit bg-page-gradient max-sm:pb-20">
      <Header />

      <main className="px-6 py-8 max-xxl:py-5 max-sm:py-4 max-sm:px-3">
        <div className="flex max-sm:justify-between mb-6 max-xxl:mb-4 max-sm:items-center">
          <h1 className="text-fluid-2xl text-[#173151] font-kollektif font-normal leading-[1.002] tracking-[-0.02em]">
            Check-In
          </h1>
          <button className="flex sm:hidden items-center whitespace-nowrap gap-2 bg-[#7d97b5] text-white px-5 py-3 rounded-full text-sm shadow-md">
            <MessageSquare size={16} />
            <span>Text Class</span>
          </button>
        </div>

        {/* TOP FILTER BAR */}
        {/* Stacks until lg, not just sm: between 640px and 1024px (every
            tablet) the old max-sm: breakpoints stopped applying, so the row
            kept its desktop fixed widths and overflowed. */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6 max-xxl:mb-3 xl:p-6 px-4 py-4 bg-[#FFFFFF80] rounded-3xl">
          {/* SEARCH BAR */}
          <div className="flex items-center bg-[#f9fafb] h-12 rounded-full shadow-sm max-xl:py-2 px-4 py-3 w-full">
            <Search size={18} className="text-[#7c7c7c] mr-2" />
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder:text-[#7c7c7c]"
            />
          </div>

          {/* DROPDOWN + TEXT BUTTON CONTAINER */}
          <div className="flex items-center gap-3 w-full lg:w-auto">
            {/* Class Selector Dropdown */}
            <div className="flex-1 lg:flex-none relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                disabled={loadingClasses}
                className="w-full lg:w-[316px] h-12 flex justify-between items-center gap-2
                           bg-[#FFFFFF66] px-6 py-[14px] rounded-full
                           shadow-sm text-fluid-base font-medium text-nuetral-200"
              >
                {/* Class names are long ("CSF School Academy - Cornelius
                    Elementary"); without truncation they shoved the chevron
                    outside the pill on narrow screens. */}
                <span className="truncate text-left">
                  {loadingClasses
                    ? "Loading classes..."
                    : selectedClass
                      ? selectedClass.name
                      : "Select a class"}
                </span>

                <svg
                  className={`w-4 h-4 shrink-0 text-[#7c7c7c] transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && classOptions.length > 0 && (
                <div className="absolute mt-0 w-full lg:w-[316px] max-h-72 overflow-y-auto z-40 bg-[#FFF] shadow-md border border-gray-200 rounded-xl">
                  {classOptions.map((classItem) => (
                    <button
                      key={classItem.id}
                      onClick={() => handleClassChange(classItem)}
                      className={`w-full text-nuetral-200 font-semibold border-b-border-light shadow-sm text-left px-6 py-4 text-[15px] max-xl:text-sm hover:bg-gray-100 ${
                        selectedClass?.id === classItem.id ? "bg-gray-50" : ""
                      }`}
                    >
                      {classItem.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* TEXT CLASS BUTTON */}
            <div className="shrink-0">
              <button className="flex max-sm:hidden items-center justify-center w-[150px] h-12 whitespace-nowrap gap-2 bg-btn-secondary font-semibold text-white px-5 py-3 rounded-full text-sm lg:text-[16px] shadow-md">
                <MessageSquare size={18} />
                <span>Text Class</span>
              </button>
            </div>
          </div>
        </div>

        {/* Student List */}
        {isFirstLoadForClass ? (
          <div className="bg-white/60 p-4 lg:p-7 rounded-3xl shadow-md backdrop-blur-md border">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-4 bg-white/30 rounded-xl"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !selectedClass ? (
          <div className="bg-white/60 p-8 lg:p-12 rounded-3xl shadow-md backdrop-blur-md border text-center">
            <div className="text-gray-400 mb-2">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-600 mb-1">
              No Class Selected
            </h3>
            <p className="text-gray-500 text-sm">
              Please select a class from the dropdown above to view students.
            </p>
          </div>
        ) : students.length === 0 ? (
          <div className="bg-white/60 p-8 lg:p-12 rounded-3xl shadow-md backdrop-blur-md border text-center">
            <div className="text-gray-400 mb-2">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-600 mb-1">
              No Students Enrolled
            </h3>
            <p className="text-gray-500 text-sm">
              {checkInError
                ? "There was an error loading students. Please try refreshing the page."
                : `No students are currently enrolled in ${selectedClass?.name || "this class"}.`}
            </p>
            <button
              onClick={() => refetchStatus()}
              className="mt-4 px-4 py-2 bg-btn-secondary text-white rounded-lg text-sm font-medium hover:bg-btn-secondary/90 transition-colors"
            >
              Refresh
            </button>
          </div>
        ) : (
          <StudentList
            key={selectedClass.id}
            students={students}
            search={search}
            sort={sort}
            setSort={setSort}
            perPage={perPage}
            setPerPage={changePerPage}
            onOpen={handleOpenModal}
            onCheckIn={handleCheckIn}
            checkingIn={checkingIn}
          />
        )}
      </main>

      {/* Modal */}
      {openModal && selectedStudent && (
        <StudentDetailsModal
          student={selectedStudent}
          onClose={() => {
            setOpenModal(false);
            setSelectedStudent(null);
          }}
        />
      )}

      <Footer isFixed={true} mobileHidden={true} />
    </div>
  );
};

export default CheckIn;
